// presets.ts — Per-PlanetKind terrain recipes: noise layer stacks (layer 0 = a
// Simple continent mask, layer 1+ = ridged/simple detail masked by it) plus the
// biome colour ramp. Gas giants use a banded, near-zero-displacement treatment.
import type { PlanetKind } from '../../types';
import { defaultLayer } from './noiseFilters';
import type { NoiseLayerSettings } from './noiseFilters';
import type { BiomeRamp } from './biome';

export interface KindPreset {
  layers: NoiseLayerSettings[];
  ramp: BiomeRamp;
  /** Radius-relative relief budget (peaks ~ baseRadius * displacementScale), further
   *  scaled by RELIEF_BUDGET_SCALE in terrain.ts — keep these as relative roughness
   *  between kinds; tune overall height there. */
  displacementScale: number;
  /** How strongly land colour pulls toward the server `color`. */
  tintPercent: number;
}

export type PlanetKindConfig = Record<PlanetKind, KindPreset>;

// Continent mask: low-freq Simple, minValue carves out oceans (negative => sea).
const continent = (overrides: Partial<NoiseLayerSettings> = {}): NoiseLayerSettings =>
  defaultLayer({
    filterType: 'simple',
    numLayers: 4,
    baseRoughness: 0.9,
    roughness: 2.1,
    persistence: 0.5,
    strength: 1,
    minValue: 1.05,
    ...overrides,
  });

// Ridged mountains, masked by the continent so ranges hug landmasses.
const mountains = (overrides: Partial<NoiseLayerSettings> = {}): NoiseLayerSettings =>
  defaultLayer({
    filterType: 'ridged',
    useFirstLayerAsMask: true,
    numLayers: 5,
    baseRoughness: 2.2,
    roughness: 2.3,
    persistence: 0.45,
    strength: 1.4,
    minValue: 0.9,
    weightMultiplier: 0.8,
    centre: [1.7, 0.4, 3.1],
    ...overrides,
  });

const RELIEF_STRENGTH: Record<'low' | 'medium' | 'high', number> = {
  low: 0.7,
  medium: 1.4,
  high: 1.9,
};

// Standard rocky/terran layer stack: a continent mask + ridged mountains whose
// strength scales with the world's relief tier. Sea/ice coverage is set by the
// ramp's seaLevel01 + snow line, so every terran kind shares one terrain recipe.
const terran = (relief: 'low' | 'medium' | 'high'): NoiseLayerSettings[] => [
  continent(),
  mountains({ strength: RELIEF_STRENGTH[relief] }),
];

export const PLANET_PRESETS: PlanetKindConfig = {
  tropical: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.16,
    ramp: { seaLevel01: 0.5, oceanDepthFalloff: 4,
      deepOcean: '#062b4a', shallowOcean: '#1fb6c9', beach: '#e6dca6',
      grassLow: '#1f8a3c', grassHigh: '#3fb85a', rockLow: '#5c6e3a', rockHigh: '#7d8a52', snow: '#f2f7f2',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.97, snowHeightPole: 0.85 },
  },
  continental: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.16,
    ramp: { seaLevel01: 0.44, oceanDepthFalloff: 4,
      deepOcean: '#0a2a4f', shallowOcean: '#2378a0', beach: '#cdb98a',
      grassLow: '#3a6e34', grassHigh: '#6e7a3e', rockLow: '#6b5d4a', rockHigh: '#8c8072', snow: '#f4f6fb',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.9, snowHeightPole: 0.5 },
  },
  ocean: {
    layers: terran('low'),
    displacementScale: 0.035,
    tintPercent: 0.22,
    ramp: { seaLevel01: 0.72, oceanDepthFalloff: 4.5,
      deepOcean: '#041830', shallowOcean: '#1d77a6', beach: '#cdb98a',
      grassLow: '#37663a', grassHigh: '#5a8a4e', rockLow: '#5e6b63', rockHigh: '#7d8a82', snow: '#eef6f8',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.95, snowHeightPole: 0.7 },
  },
  savanna: {
    layers: terran('low'),
    displacementScale: 0.035,
    tintPercent: 0.2,
    ramp: { seaLevel01: 0.32, oceanDepthFalloff: 4,
      deepOcean: '#1f4a5a', shallowOcean: '#3f8a8a', beach: '#d8bd7e',
      grassLow: '#c4a04a', grassHigh: '#a07a32', rockLow: '#8a6b3f', rockHigh: '#a8895a', snow: '#efe4c8',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.97, snowHeightPole: 0.82 },
  },
  arid: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.24,
    ramp: { seaLevel01: 0.2, oceanDepthFalloff: 4,
      deepOcean: '#5a3a22', shallowOcean: '#8a5a32', beach: '#c08a52',
      grassLow: '#a85a30', grassHigh: '#8a3f22', rockLow: '#6f3a24', rockHigh: '#9c5a38', snow: '#d8c2a0',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.99, snowHeightPole: 0.92 },
  },
  desert: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.25,
    ramp: { seaLevel01: 0, oceanDepthFalloff: 4,
      deepOcean: '#8a6a3f', shallowOcean: '#a8865a', beach: '#cdab6a',
      grassLow: '#dcb877', grassHigh: '#c79a52', rockLow: '#a8783f', rockHigh: '#c99a5f', snow: '#ece0c4',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.99, snowHeightPole: 0.97 },
  },
  arctic: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.14,
    ramp: { seaLevel01: 0.36, oceanDepthFalloff: 4,
      deepOcean: '#3f6f8c', shallowOcean: '#8fc4d6', beach: '#cfe2ea',
      grassLow: '#9fb0b8', grassHigh: '#c2d4dc', rockLow: '#7a8a90', rockHigh: '#a8b8be', snow: '#ffffff',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.18, snowHeightPole: 0 },
  },
  tundra: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.16,
    ramp: { seaLevel01: 0.34, oceanDepthFalloff: 4,
      deepOcean: '#2e4452', shallowOcean: '#587a84', beach: '#857f6b',
      grassLow: '#6b6a46', grassHigh: '#918a64', rockLow: '#474640', rockHigh: '#726e62', snow: '#e8eef2',
      beachWidth: 0.05, maxFlatHeight: 0.5,
      snowHeightEquator: 0.4, snowHeightPole: 0.08 },
  },
  alpine: {
    layers: terran('high'),
    displacementScale: 0.11,
    tintPercent: 0.1,
    ramp: { seaLevel01: 0.22, oceanDepthFalloff: 4,
      deepOcean: '#2f5a6a', shallowOcean: '#5aa0b0', beach: '#9aa07e',
      grassLow: '#5a7a48', grassHigh: '#7a8a5a', rockLow: '#6e6e70', rockHigh: '#9c9ca0', snow: '#fbfdff',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.5, snowHeightPole: 0.3 },
  },
  volcanic: {
    // A dark basalt world: the whole crust ramp is near-black (the "seas" too — they
    // are cooled lava plains, not glowing pools). All of the lava is the emissive
    // spiderweb of fissures drawn in surfaceShader, which the dark crust sets off.
    layers: terran('high'),
    displacementScale: 0.11,
    tintPercent: 0.1,
    ramp: { seaLevel01: 0.18, oceanDepthFalloff: 3,
      deepOcean: '#1a0d08', shallowOcean: '#241409', beach: '#1f130e',
      grassLow: '#120c09', grassHigh: '#1f1610', rockLow: '#0a0706', rockHigh: '#241c17', snow: '#322e29',
      beachWidth: 0.04, maxFlatHeight: 0.5,
      snowHeightEquator: 0.99, snowHeightPole: 0.99 },
  },
  barren_dry: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.12,
    ramp: { seaLevel01: 0, oceanDepthFalloff: 4,
      deepOcean: '#5a5042', shallowOcean: '#6f6452', beach: '#8a7d66',
      grassLow: '#9c8e74', grassHigh: '#b0a288', rockLow: '#7a6f5c', rockHigh: '#a89a82', snow: '#c2b8a4',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.99, snowHeightPole: 0.99 },
  },
  barren_cold: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.12,
    ramp: { seaLevel01: 0, oceanDepthFalloff: 4,
      deepOcean: '#2a323a', shallowOcean: '#3a444c', beach: '#4f5a62',
      grassLow: '#5a656e', grassHigh: '#6f7a82', rockLow: '#454e56', rockHigh: '#69747c', snow: '#8a949c',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.99, snowHeightPole: 0.99 },
  },
  broken: {
    layers: terran('high'),
    displacementScale: 0.11,
    tintPercent: 0.1,
    ramp: { seaLevel01: 0.08, oceanDepthFalloff: 4,
      deepOcean: '#5a1a12', shallowOcean: '#7a2a18', beach: '#2a2628',
      grassLow: '#33333a', grassHigh: '#42424a', rockLow: '#22222a', rockHigh: '#4f4f58', snow: '#5a5a64',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.99, snowHeightPole: 0.99 },
  },
  frozen: {
    layers: terran('low'),
    displacementScale: 0.035,
    tintPercent: 0.1,
    ramp: { seaLevel01: 0, oceanDepthFalloff: 4,
      deepOcean: '#aac4d2', shallowOcean: '#c2d8e2', beach: '#d4e4ec',
      grassLow: '#dceaf0', grassHigh: '#eaf4f8', rockLow: '#bcccd6', rockHigh: '#d8e6ee', snow: '#ffffff',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.1, snowHeightPole: 0 },
  },
  molten: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.1,
    ramp: { seaLevel01: 0.5, oceanDepthFalloff: 4,
      deepOcean: '#ff6e10', shallowOcean: '#ffd24a', beach: '#5a261a',
      grassLow: '#3a221c', grassHigh: '#2a1a16', rockLow: '#1c1210', rockHigh: '#4a322c', snow: '#6e544c',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.99, snowHeightPole: 0.99 },
  },
  toxic: {
    layers: terran('medium'),
    displacementScale: 0.07,
    tintPercent: 0.18,
    ramp: { seaLevel01: 0.48, oceanDepthFalloff: 4,
      deepOcean: '#3a4a1c', shallowOcean: '#8aa83a', beach: '#9c9a4a',
      grassLow: '#8a9a32', grassHigh: '#6e7a28', rockLow: '#5a5a30', rockHigh: '#8a8a4a', snow: '#d6dc9a',
      beachWidth: 0.05, maxFlatHeight: 0.55,
      snowHeightEquator: 0.98, snowHeightPole: 0.9 },
  },
  gas: {
    // Near-zero displacement: a smooth ball with subtle turbulence so the
    // silhouette isn't perfectly round; colour is purely latitude bands.
    layers: [
      defaultLayer({
        filterType: 'simple',
        numLayers: 3,
        baseRoughness: 1.6,
        roughness: 2.2,
        persistence: 0.5,
        strength: 0.4,
        minValue: 0,
      }),
    ],
    displacementScale: 0.012,
    tintPercent: 0.3,
    ramp: {
      seaLevel01: 0, oceanDepthFalloff: 1,
      deepOcean: '#000000', shallowOcean: '#000000', beach: '#000000',
      grassLow: '#000000', grassHigh: '#000000', rockLow: '#000000', rockHigh: '#000000',
      snow: '#000000', beachWidth: 0, maxFlatHeight: 1,
      snowHeightEquator: 1, snowHeightPole: 1,
      bands: {
        stops: ['#caa46a', '#e8d3a1', '#b88a52', '#f0e2c0', '#9c6b3f', '#dcc596'],
        sharpness: 0.35,
      },
    },
  },
};
